import React from 'react';
import { DashCircle, PlusCircle } from 'react-bootstrap-icons';
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from 'react-hook-form';
import { CareMapprTextarea } from '../../../../Inputs/textarea';

interface TaperFormProps {
  taperFields: Record<'id', string>[];
  formWatch: UseFormWatch<any>;
  setFormValue: UseFormSetValue<any>;
  control: Control<any>;
  path: (path: string) => string;
  handleAddTaperSchedule: () => void;
}

interface TaperColumn {
  title: string;
}

interface TaperScheduleItem {
  values: string[];
}

type TaperFormValues = {
  regimen: {
    taper: {
      dosingSchedule: string;
      taperTitle: string;
      taperHeaders: TaperColumn[];
      taperSchedule: TaperScheduleItem[];
    };
  };
};

const TaperForm = ({
  taperFields,
  formWatch,
  setFormValue,
  path,
  control,
  handleAddTaperSchedule,
}: TaperFormProps) => {
  const {
    fields: taperHeadersFields,
    append: appendColumn,
    remove: removeColumn,
    replace: replaceHeaders,
  } = useFieldArray<TaperFormValues, 'regimen.taper.taperHeaders'>({
    control,
    name: path('regimen.taper.taperHeaders') as 'regimen.taper.taperHeaders',
  });

  const handleRemoveColumn = (index: number) => {
    removeColumn(index);
    if (Array.isArray(taperScheduleValues)) {
      taperScheduleValues.forEach((item, i) => {
        const values = Array.isArray(item?.values) ? [...item.values] : [];
        if (index >= 0 && index < values.length) {
          values.splice(index, 1);
          updateSchedule(i, { ...item, values });
        }
      });
    }
  };

  const { update: updateSchedule } = useFieldArray<
    TaperFormValues,
    'regimen.taper.taperSchedule'
  >({
    control,
    name: path('regimen.taper.taperSchedule') as 'regimen.taper.taperSchedule',
  });

  const taperScheduleValues = useWatch({
    control,
    name: path('regimen.taper.taperSchedule') as 'regimen.taper.taperSchedule',
  }) as TaperScheduleItem[];

  const columns =
    (useWatch({
      control,
      name: path('regimen.taper.taperHeaders') as 'regimen.taper.taperHeaders',
    }) as TaperColumn[]) || [];

  React.useEffect(() => {
    if (Array.isArray(columns) && columns.length > 0) {
      const first = (columns as any[])[0];
      if (typeof first === 'string') {
        const normalized = (columns as any[]).map((h) =>
          typeof h === 'string' ? { title: h } : { title: h?.title ?? '' },
        );
        replaceHeaders(normalized as any);
      }
    }
  }, [columns, replaceHeaders]);

  // Ensure at least one header exists when there is at least one schedule row
  React.useEffect(() => {
    if (
      Array.isArray(taperFields) &&
      taperFields.length > 0 &&
      taperHeadersFields.length === 0
    ) {
      appendColumn({ title: '' });
    }
  }, [taperFields, taperHeadersFields, appendColumn]);

  const handleAddColumn = React.useCallback(() => {
    appendColumn({ title: '' });
  }, [appendColumn]);

  const renderAutoResizeField = (
    name: string,
    controlId: string,
    placeholder: string,
    options: {
      rows?: number;
      inputClassName?: string;
      defaultValue?: string;
    } = {},
  ) => {
    const {
      rows = 1,
      inputClassName = 'med-input',
      defaultValue = '',
    } = options;
    const value = formWatch(name);
    return (
      <CareMapprTextarea
        autoResize
        rows={rows}
        controlId={controlId}
        placeholder={placeholder}
        inputClassName={inputClassName}
        value={String(value ?? defaultValue)}
        onChange={(e) => {
          setFormValue(name, e.target.value, { shouldDirty: true });
        }}
      />
    );
  };

  return (
    <>
      <div className="my-3">
        <label className="txtDropdownLabel">Dosing schedule</label>
        <div className="divSelect">
          {renderAutoResizeField(
            path(`regimen.taper.dosingSchedule`),
            'regimen-taper-dosing-schedule',
            'Start typing ...',
          )}
        </div>
      </div>
      <div className="my-3">
        <label className="txtDropdownLabel">Taper title</label>
        <div className="divSelect">
          {renderAutoResizeField(
            path(`regimen.taper.taperTitle`),
            'regimen-taper-title',
            'Start typing ...',
            { defaultValue: 'This is your tapering schedule' },
          )}
        </div>
      </div>
      {taperFields.map((field, index) => {
        return (
          <div className="med-form-container-row" key={`${field.id}-${index}`}>
            {taperHeadersFields.map((colField, colIndex) => (
              <div className="filterItem" key={colField.id}>
                {index === 0 && (
                  <div className="d-flex flex-row align-items-center gap-2 mb-1">
                    {renderAutoResizeField(
                      path(`regimen.taper.taperHeaders.${colIndex}.title`),
                      `regimen-taper-header-${colIndex}`,
                      'Enter title...',
                      { inputClassName: 'taper-title-input' },
                    )}
                    {colIndex > 0 && (
                      <button
                        className="clickable d-flex gap-4"
                        type="button"
                        onClick={() => handleRemoveColumn(colIndex)}
                      >
                        <DashCircle color="#D05B5B" size={18} />
                      </button>
                    )}
                  </div>
                )}
                <div className="divSelect">
                  {renderAutoResizeField(
                    path(
                      `regimen.taper.taperSchedule.${index}.values.${colIndex}`,
                    ),
                    `regimen-taper-schedule-${index}-${colIndex}`,
                    'Start typing...',
                  )}
                </div>
              </div>
            ))}
            {index === 0 ? (
              <button
                type="button"
                style={{ marginBottom: 'auto' }}
                className="additional-button px-1"
                onClick={handleAddColumn}
              >
                <PlusCircle color="#654b78" size={18} />
              </button>
            ) : (
              <div style={{ width: '24px' }} />
            )}
          </div>
        );
      })}
      <div
        className="additional-button-container"
        style={{ marginLeft: '15px', marginTop: '8px' }}
      >
        <button
          className="additional-button"
          type="button"
          onClick={handleAddTaperSchedule}
        >
          <i className="fa-solid fa-plus fa-md" />
          <span>Add line</span>
        </button>
      </div>
    </>
  );
};

export default TaperForm;
